/**
 * Problem/Trust Section Component
 * Addresses why OKRs often fail - with scroll-triggered animations
 */

import React, { useState, useEffect, useRef } from 'react';
import { Section } from './Section';
import { AlertTriangle, X, ArrowRight } from 'lucide-react';

const problems = [
  'Objectives that sound good but don\'t guide decisions',
  'Key Results that track effort, not outcomes',
  'Leaders delegating ownership instead of embodying it',
  'Reviews that happen, but learning does not',
  'Tools that collect data but don\'t drive focus'
];

interface ProblemItemProps {
  text: string;
  index: number;
  isVisible: boolean;
}

const ProblemItem: React.FC<ProblemItemProps> = ({ text, index, isVisible }) => {
  const [showStrike, setShowStrike] = useState(false);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => setShowStrike(true), 600 + index * 150);
      return () => clearTimeout(timer);
    }
  }, [isVisible, index]);

  return (
    <div
      className={`flex items-center gap-4 bg-white rounded-xl p-5 border-2 border-stone-200 transition-all duration-500 ${
        isVisible
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 -translate-x-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
        showStrike ? 'bg-error-100' : 'bg-stone-100'
      }`}>
        <X className={`w-5 h-5 transition-all duration-300 ${
          showStrike ? 'text-error-500' : 'text-stone-400'
        }`} />
      </div>
      <p className="text-lg text-ink relative">
        <span className={`relative ${showStrike ? 'text-ink-muted' : 'text-ink'} transition-colors duration-300`}>
          {text}
          {/* Strike-through line */}
          <span
            className={`absolute left-0 top-1/2 h-0.5 bg-error-400 transition-all duration-700 ease-out ${
              showStrike ? 'w-full' : 'w-0'
            }`}
          />
        </span>
      </p>
    </div>
  );
};

export const ProblemSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Section id="problem" background="cream" padding="xl">
      <div ref={sectionRef} className="max-w-5xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center gap-2 bg-warning-100 text-warning-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <AlertTriangle className="w-4 h-4" />
            <span>The Reality Check</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-ink mb-4">
            Why OKRs Often Fail
          </h2>
          <p className="text-xl text-ink-muted max-w-2xl mx-auto">
            OKRs fail not because the framework is flawed—but because implementation is shallow.
          </p>
        </div>

        {/* Problems Grid */}
        <div className="grid gap-4 mb-12">
          {problems.map((problem, index) => (
            <ProblemItem
              key={index}
              text={problem}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>

        {/* Solution Statement */}
        <div className={`relative transition-all duration-700 delay-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-8 md:p-12 text-white text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

            <div className="relative z-10">
              <p className="text-2xl md:text-3xl font-display leading-relaxed mb-6">
                Without clarity and cadence,
                <br />
                <span className="text-white/90">OKRs become paperwork.</span>
              </p>
              <p className="text-lg md:text-xl text-white/90 mb-8">
                OKR Hub treats OKRs as a <span className="font-semibold text-white">management discipline</span>—not a template.
              </p>
              <button
                onClick={() => scrollToSection('services')}
                className="inline-flex items-center gap-2 bg-white text-primary-600 px-6 py-3 rounded-xl font-semibold hover:bg-white/90 hover:-translate-y-0.5 transition-all duration-300"
              >
                See Our Approach
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
